import { Component, computed, ElementRef, HostListener, inject, signal, TemplateRef, ChangeDetectionStrategy, viewChild } from '@angular/core'
import { environment } from '../../../environments/environment'
import { themeConfigs, ThemeName } from '../../themes/theme-config'
import { ThemeService } from '../../services/theme.service'
import { AuthService } from '../../services/auth.service'
import { BaseButtonComponent } from '../../shared/components/base/base-button/base-button.component'
import { ModalService, ModalTypes } from '@/services/modal.service'
import { DefaultModalData } from '@/shared/components/modals/default-modal.component'
import { ModalParams, ModalServiceV2 } from '@/services/modal-v2.service'
import { LoggerService } from '@/services/logger.service'
import { LoggerModule } from '@/config/logger-modules'
import { BetslipWorkbenchComponent } from './betslip-workbench/betslip-workbench.component'
import { PlayerLiabilityMock } from './betslip-workbench/player-liability-mock'
import { PlayerBettingLimitsMock } from './betslip-workbench/player-betting-limits-mock'
import { WorkbenchLimitsState } from './betslip-workbench/betslip-workbench.store'
import { LiabilityLimitsState } from './betslip-workbench/liability-limits.state'

@Component({
	selector: 'dev-tools',
	imports: [BaseButtonComponent, BetslipWorkbenchComponent],
	templateUrl: './dev-tools.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./dev-tools.component.css']
})
export class DevToolsComponent {
	readonly devToolsContainerRef = viewChild.required<ElementRef<HTMLElement>>('devToolsContainer')
	readonly modalTitleTemplate = viewChild.required<TemplateRef<unknown>>('modalTitleTemplate')
	readonly modalBodyTemplate = viewChild.required<TemplateRef<unknown>>('modalBodyTemplate')

	@HostListener('document:keydown', ['$event'])
	handleKeyboardShortcut(event: KeyboardEvent) {
		if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd') {
			this.togglePanel()
			event.preventDefault()
		}
	}
	@HostListener('document:click', ['$event'])
	onOutsideClick(event: MouseEvent): void {
		const target = event.target as HTMLElement
		if (this.isOpen && !this.devToolsContainerRef()?.nativeElement.contains(target)) {
			this.isOpen = false
		}
	}

	public isOpen = false
	public showBetslipWorkbench = false
	public themeOptions = Object.keys(themeConfigs) as (keyof typeof themeConfigs)[]
	public showToggleButton = false
	public modal = {
		showTestModal: true
	}

	private readonly loggerService = inject(LoggerService)
	private readonly workbenchLimits = inject(WorkbenchLimitsState)
	private readonly liabilityLimits = inject(LiabilityLimitsState)
	private readonly loggerFilter = signal('')

	public readonly loggerActiveCount = this.loggerService.activeCount
	public readonly loggerTotalCount = this.loggerService.totalCount

	// Registered modules grouped for display, narrowed by the filter field
	public readonly loggerGroups = computed(() => {
		const filter = this.loggerFilter().trim().toLowerCase()

		return this.loggerService.moduleGroups()
			.map((group) => ({
				group: group.group,
				modules: group.modules.filter((module) => module.name.toLowerCase().includes(filter))
			}))
			.filter((group) => group.modules.length > 0)
	})

	constructor(
		public themeService: ThemeService,
		public authService: AuthService,
		private modalService: ModalService,
		private modalServiceV2: ModalServiceV2
	) {
		// TODO: DEV-MOCK — the dev limits mocks on the player's betslip; see betslip-workbench/README.md
		inject(PlayerLiabilityMock)
		inject(PlayerBettingLimitsMock)
	}

	togglePanel() {
		this.isOpen = !this.isOpen
	}

	openBetslipWorkbench() {
		this.isOpen = false
		this.showBetslipWorkbench = true
	}

	/** The workbench's limits and player back to their defaults, on both betslips, with nothing stored. */
	resetBetslipLimits() {
		this.workbenchLimits.reset()
		this.liabilityLimits.reset()
	}

	onLoggerFilterChange(event: Event) {
		this.loggerFilter.set((event.target as HTMLInputElement).value)
	}

	toggleLoggerModule(module: LoggerModule) {
		this.loggerService.toggle(module)
	}

	enableAllLoggerModules() {
		this.loggerService.setAll(true)
	}

	disableAllLoggerModules() {
		this.loggerService.setAll(false)
	}

	resetLoggerModules() {
		this.loggerService.resetToEnvironment()
	}

	onThemeChange(event: Event) {
		const theme = ((event.target as HTMLSelectElement).value as ThemeName)
		this.themeService.setTheme(theme)
	}

	handleLogin() {
		const { username, password } = environment
		if (username && password) {
			this.authService.login({ username: username, password: password })
		}
	}

	/**
	 * The modal an anonymous player gets from the favorite toggle, the Play
	 * button and the Favorites route. Opened directly rather than through
	 * AuthService.promptAuthenticationIfNotLoggedIn, which is a no-op once
	 * there is a session - this has to be testable while logged in.
	 */
	openAuthPrompt() {
		this.isOpen = false
		void this.modalService.open('login')
	}

	openModalV1(type: ModalTypes)	{
		let data: DefaultModalData | null = null
		if (type === 'default') {
			data = {
				title: 'Test Modal',
				body: 'This is a test modal from Dev Tools.'
			}
		}
		// Open the modal with the specified type and data (if applicable)
		this.modalService.open(type, data)
	}

	openModalV2(type: 'text' | 'template')	{
		let params!: ModalParams
		if (type === 'text') {
			params = {
				title: 'Text Modal',
				body: 'This modal uses simple text for it\'s content.',
				translate: false
			}
		} else if (type === 'template') {
			params = {
				title: this.modalTitleTemplate(),
				body: this.modalBodyTemplate(),
				translate: false
			}
		}
		this.modalServiceV2.open(params)
	}
}
